import Box from '@mui/material/Box';
import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from 'axios';
import Modal from '@mui/material/Modal';
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import FormField from "layouts/pages/account/components/FormField";
import MDButton from "components/MDButton";
import { generateParoslyAlert } from 'modules/paroslyCasting';
import config from "../../../../config";
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';

function DataQualityForm({ setErrorSB, setSuccessSB }) {
  const { t } = useTranslation();
  const dqCorrelations = [
    {
      label: t('notifications.dataQualityForm.dqSources.detectors'), value: 'detectors', subs: [
        { label: t('notifications.dataQualityForm.dqSources.detectorsSubs.provider'), value: 'provider' },
        { label: t('notifications.dataQualityForm.dqSources.detectorsSubs.route'), value: 'route' },
        { label: t('notifications.dataQualityForm.dqSources.detectorsSubs.intersection'), value: 'intersection' },
        { label: t('notifications.dataQualityForm.dqSources.detectorsSubs.detector'), value: 'detector' }
      ]
    },
    {
      label: t('notifications.dataQualityForm.dqSources.fcdBluetooth'), value: 'speedTravel', subs: [
        { label: t('notifications.dataQualityForm.dqSources.fcdBluetoothSubs.tomtom'), value: 'tomtomfcd' },
        { label: t('notifications.dataQualityForm.dqSources.fcdBluetoothSubs.taxi'), value: 'taxifcd' },
        { label: t('notifications.dataQualityForm.dqSources.fcdBluetoothSubs.bluetooth'), value: 'bluetooth' },
      ]
    }
  ];

  const [alertType, setAlertType] = useState("provider");
  const [dataSource, setDataSource] = useState("detectors");
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [selectedThreshold, setSelectedThreshold] = useState(0);
  const [selectedSeverity, setSelectedSeverity] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [selectedMetric, setSelectedMetric] = useState('');
  const [location, setlocation] = useState("None");

  const [optionsDrop, setOptionsDrop] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  const [detectorOptions, setDetectorOptions] = useState([]);
  const [isLoadingDetectors, setIsLoadingDetectors] = useState(false);

  // Submitted verbatim to the Parosly/Prometheus alert payload — keep these values in English.
  const severities = ["Warning", "Critical", "Emergency", "Alert", "Error", "Info", "Notice", "Debug"];
  const providerStaticOptions = ["SCAE", "SWARKO", "SIEMENS"];

  const alernName = {
    "provider": "Provider",
    "route": "Route",
    "intersection": "Intersection",
    "detector": "Detector",
    "tomtomfcd": "TomTom FCD",
    "taxifcd": "Taxi FCD",
    "bluetooth": "Bluetooth",
  };

  // Display-only translations for the values above — never sent to the API.
  const severityLabels = {
    Warning: t('notifications.dataQualityForm.severityOptions.Warning'),
    Critical: t('notifications.dataQualityForm.severityOptions.Critical'),
    Emergency: t('notifications.dataQualityForm.severityOptions.Emergency'),
    Alert: t('notifications.dataQualityForm.severityOptions.Alert'),
    Error: t('notifications.dataQualityForm.severityOptions.Error'),
    Info: t('notifications.dataQualityForm.severityOptions.Info'),
    Notice: t('notifications.dataQualityForm.severityOptions.Notice'),
    Debug: t('notifications.dataQualityForm.severityOptions.Debug'),
  };
  const metricLabels = {
    Completeness: t('notifications.dataQualityForm.metricOptions.Completeness'),
    Validity: t('notifications.dataQualityForm.metricOptions.Validity'),
  };
  const alernNameDisplay = {
    provider: t('notifications.dataQualityForm.fieldLabels.provider'),
    route: t('notifications.dataQualityForm.fieldLabels.route'),
    intersection: t('notifications.dataQualityForm.fieldLabels.intersection'),
    detector: t('notifications.dataQualityForm.fieldLabels.detector'),
    tomtomfcd: t('notifications.dataQualityForm.fieldLabels.tomtomfcd'),
    taxifcd: t('notifications.dataQualityForm.fieldLabels.taxifcd'),
    bluetooth: t('notifications.dataQualityForm.fieldLabels.bluetooth'),
  };

  const isFcdOrBt = ["tomtomfcd", "taxifcd", "bluetooth"].includes(alertType);
  const isRoutes = location === "router";

  const metricOptions = isFcdOrBt ? ["Completeness"] : ["Completeness", "Validity"];

  useEffect(() => {
    if (isFcdOrBt) {
      setSelectedMetric("Completeness");
    } else if (!selectedMetric) {
      setSelectedMetric("Completeness");
    }
  }, [isFcdOrBt]);

  function getSubSourceValues(targetValue) {
    const target = dqCorrelations.find(item => item.value === targetValue);
    return target?.subs ?? [];
  }

  const handleChangeTop = (event, newValue) => {
    setDataSource(newValue);
    const subs = getSubSourceValues(newValue);
    setAlertType(subs.length > 0 ? subs[0].value : "");
    setlocation("None");
    setSelectedProvider('');
    setOptionsDrop([]);
    setDetectorOptions([]);
  };

  const handleChangeType = (event, newValue) => {
    setAlertType(newValue);
    setlocation("None");
    setSelectedProvider('');
    setOptionsDrop([]);
    setDetectorOptions([]);
  };

  const handleChangeLocation = (e) => {
    setlocation(e.target.value);
    setSelectedProvider('');
    setOptionsDrop([]);
  };

  const handleInputChange = (type, value) => {
    if (type === "changeProv") {
      setSelectedProvider(value);
    } else if (type === "changeThreshold") {
      let num = Number(value);
      if (Number.isNaN(num)) num = 0;
      if (num > 100) num = 100;
      if (num < 0) num = 0;
      setSelectedThreshold(num);
    } else if (type === "changeSev") {
      setSelectedSeverity(value);
    } else if (type === "changeName") {
      setSelectedName(value);
    } else if (type === "changeMetric") {
      setSelectedMetric(value);
    }
  };

  const normalizeLabel = (s) => (s || "").replace(/\s+/g, " ").trim();

  // Fetch FCD/BT routes
  const fetchOptions = useCallback(async () => {
    if (!isFcdOrBt || !isRoutes) {
      setOptionsDrop([]);
      return;
    }

    setIsLoadingOptions(true);
    try {
      let url = '';
      let headers = {};
      if (alertType === 'tomtomfcd') {
        url = 'https://sora.imet.gr/api/materialized_tomtom_routes';
        headers = { 'accept-profile': 'route_monitoring' };
      } else {
        url = 'https://sora.imet.gr/api/bt_taxi_paths';
        headers = { 'accept-profile': 'open_imet' };
      }

      const res = await axios.get(url, { headers });
      const data = Array.isArray(res.data) ? res.data : [];

      const mapped = (alertType === 'tomtomfcd')
        ? data.map(d => ({ id: d.id_long_paths, label: d.name }))
        : data.map(d => ({ id: d.id_path, label: d.path_name }));

      setOptionsDrop(mapped);
    } catch (err) {
      console.log('Failed to fetch options:', err);
      setOptionsDrop([]);
    } finally {
      setIsLoadingOptions(false);
    }
  }, [alertType, isFcdOrBt, isRoutes]);

  // Fetch Detectors Data
  const fetchDetectorOptions = useCallback(async () => {
    if (dataSource !== 'detectors') {
      setDetectorOptions([]);
      return;
    }
    if (!['route', 'intersection', 'detector'].includes(alertType)) {
      setDetectorOptions([]);
      return;
    }

    setIsLoadingDetectors(true);
    try {
      let url = '';
      let mapFn;

      if (alertType === 'route') {
        url = 'https://sora.imet.gr/api/route_geom?select=route_id,name_en';
        mapFn = (d) => ({ id: d.route_id, label: normalizeLabel(d.name_en) });
      } else if (alertType === 'intersection') {
        url = 'https://sora.imet.gr/api/intersection_geom?select=intersection_id,name_en';
        mapFn = (d) => ({ id: d.intersection_id, label: normalizeLabel(d.name_en) });
      } else {
        url = 'https://sora.imet.gr/api/detector_geom?select=detector_id,detector_name';
        mapFn = (d) => ({ id: d.detector_id, label: normalizeLabel(d.detector_name) });
      }

      const res = await axios.get(url, { headers: { "Accept-Profile": "detectors" } });
      const data = Array.isArray(res.data) ? res.data : [];
      setDetectorOptions(data.map(mapFn));
    } catch (err) {
      console.log('Failed to fetch detector options:', err);
      setDetectorOptions([]);
    } finally {
      setIsLoadingDetectors(false);
    }
  }, [dataSource, alertType]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);
  useEffect(() => { fetchDetectorOptions(); }, [fetchDetectorOptions]);

  const handleSubmit = async () => {
    const dataToSend = generateParoslyAlert(
      selectedProvider,
      selectedThreshold,
      "Detectors per provider",
      selectedMetric,
      selectedSeverity,
      selectedName,
      alernName[alertType]
    );
    try {
      await axios.post(config.SYNCHROEU_URL + "/parosly/api/v1/rules", dataToSend);
      setSuccessSB(true);
    } catch (err) {
      console.log(err);
      setErrorSB(true);
    }
  };

  const discard = () => {
    setSelectedProvider(null);
    setSelectedThreshold('');
    setSelectedSeverity('');
    setSelectedName('');
    setSelectedMetric('');
    setOptionsDrop([]);
    setDetectorOptions([]);
  };

  const renderPrimaryInput = () => {
    if (isFcdOrBt && isRoutes) {
      return (
        <Autocomplete
          value={optionsDrop.find(o => o.id === selectedProvider) || null}
          options={optionsDrop}
          loading={isLoadingOptions}
          getOptionLabel={(option) => option?.label ?? ''}
          onChange={(e, option) => handleInputChange("changeProv", option ? option.id : '')}
          renderInput={(params) => (
            <MDInput {...params} variant="standard" placeholder={isLoadingOptions ? t('notifications.dataQualityForm.placeholders.loading') : t('notifications.dataQualityForm.placeholders.selectRoute')} />
          )}
        />
      );
    }

    if (dataSource === 'detectors') {
      if (alertType === 'provider') {
        return (
          <Autocomplete
            value={selectedProvider || null}
            options={providerStaticOptions}
            getOptionLabel={(option) => option || ''}
            onChange={(e, opt) => handleInputChange("changeProv", opt || '')}
            renderInput={(params) => <MDInput {...params} variant="standard" placeholder={t('notifications.dataQualityForm.placeholders.selectProvider')} />}
          />
        );
      }

      if (['route', 'intersection', 'detector'].includes(alertType)) {
        return (
          <Autocomplete
            value={detectorOptions.find(o => o.id === selectedProvider) || null}
            options={detectorOptions}
            loading={isLoadingDetectors}
            getOptionLabel={(option) => option?.label ?? ''}
            onChange={(e, option) => handleInputChange("changeProv", option ? option.id : '')}
            renderInput={(params) => (
              <MDInput {...params} variant="standard" placeholder={isLoadingDetectors ? t('notifications.dataQualityForm.placeholders.loading') : t('notifications.dataQualityForm.placeholders.selectField', { field: alernNameDisplay[alertType] })} />
            )}
          />
        );
      }
    }

    return (
      <MDInput
        type="text"
        value={selectedProvider || ''}
        style={{ width: "100%" }}
        variant="standard"
        onChange={(e) => handleInputChange("changeProv", e.target.value)}
      />
    );
  };

  return (
    <Grid container spacing={3} style={{ padding: "10px" }}>
      {/* Tabs */}
      <Grid item md={12} lg={7}>
        <Tabs orientation="horizontal" value={dataSource} onChange={handleChangeTop}>
          {dqCorrelations.map((item, index) => (
            <Tab key={index} label={item.label} value={item.value} style={{ padding: '8px' }} />
          ))}
        </Tabs>
      </Grid>
      <Grid item md={12} lg={7} style={{ marginTop: "-10px" }}>
        <Tabs orientation="horizontal" value={alertType} onChange={handleChangeType}>
          {getSubSourceValues(dataSource).map((item, index) => (
            <Tab key={index} label={item.label} value={item.value} style={{ padding: '8px' }} />
          ))}
        </Tabs>
      </Grid>

      {/* Location selector */}
      {isFcdOrBt && (
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <FormControl>
            <InputLabel id="select-location-label">{t('notifications.dataQualityForm.spatialLevel')}</InputLabel>
            <Select
              labelId="select-location-label"
              id="select-location"
              value={location}
              label={t('notifications.dataQualityForm.selectLocationType')}
              onChange={handleChangeLocation}
              style={{ width: '320px', paddingTop: '8px', paddingBottom: '8px' }}
            >
              <MenuItem value={"None"}>{t('common.buttons.none')}</MenuItem>
              <MenuItem value={"network"}>{t('notifications.dataQualityForm.locationOptions.network')}</MenuItem>
              <MenuItem value={"router"}>{t('notifications.dataQualityForm.locationOptions.router')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}

      {/* Main Autocomplete */}
      <Grid item xs={12} sm={12} md={12} lg={7}>
        <MDTypography component="label" variant="button" fontWeight="regular" color="text">
          {alernNameDisplay[alertType]}
        </MDTypography>
        {renderPrimaryInput()}
      </Grid>

      {/* Metric */}
      <Grid item xs={12} sm={12} md={12} lg={7}>
        <MDTypography component="label" variant="button" fontWeight="regular" color="text">
          {t('notifications.dataQualityForm.metric')}
        </MDTypography>
        <Autocomplete
          value={metricOptions.find((m) => m === selectedMetric) || null}
          options={metricOptions}
          getOptionLabel={(option) => metricLabels[option] || option}
          onChange={(e, opt) => handleInputChange("changeMetric", opt)}
          renderInput={(params) => <MDInput {...params} variant="standard" />}
        />
      </Grid>

      {/* Rule name */}
      <Grid item xs={12} sm={12} md={12} lg={7}>
        <MDTypography component="label" variant="button" fontWeight="regular" color="text">
          {t('notifications.dataQualityForm.name')}
        </MDTypography>
        <MDInput
          type="text"
          value={selectedName || ''}
          style={{ width: "100%" }}
          variant="standard"
          onChange={(e) => handleInputChange("changeName", e.target.value)}
        />
      </Grid>

      {/* Threshold + Severity on the same line */}
      <Grid item xs={12} md={12} lg={7} container spacing={2}>
        <Grid item xs={12} md={6}>
          <MDTypography component="label" variant="button" fontWeight="regular" color="text">
            {t('notifications.dataQualityForm.belowThreshold')}
          </MDTypography>
          <MDInput
            type="number"
            value={selectedThreshold}
            style={{ width: "100%" }}
            variant="standard"
            onChange={(e) => handleInputChange("changeThreshold", e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDTypography component="label" variant="button" fontWeight="regular" color="text">
            {t('notifications.dataQualityForm.severity')}
          </MDTypography>
          <Autocomplete
            value={severities.find((s) => s === selectedSeverity) || null}
            options={severities}
            getOptionLabel={(option) => severityLabels[option] || option}
            onChange={(e, opt) => handleInputChange("changeSev", opt)}
            renderInput={(params) => <MDInput {...params} variant="standard" />}
          />
        </Grid>
      </Grid>

      {/* Actions */}
      <Grid mt={3} item xs={12} sm={12} md={12} lg={7}>
        <MDBox display="flex" justifyContent="space-around" alignItems="center">
          <MDButton variant="gradient" color="secondary" onClick={discard} sx={{ mr: 2 }}>
            {t('notifications.dataQualityForm.discard')}
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleSubmit}>
            {t('common.buttons.submit')}
          </MDButton>
        </MDBox>
      </Grid>
    </Grid>
  );
}

export default DataQualityForm;

